import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Chatbot.css';
import { FaCommentDots, FaTimes, FaPaperPlane, FaWhatsapp } from 'react-icons/fa';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Hi there! 👋 How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Fetch FAQs to use as knowledge base
    const fetchFAQs = async () => {
      try {
        const res = await axios.get(window.API_URL + '/faq');
        if (Array.isArray(res.data)) {
          setFaqs(res.data);
        } else {
          console.error("Expected array for FAQs, but got:", res.data);
          setFaqs([]);
        }
      } catch (err) {
        console.error("Error fetching FAQs:", err);
      }
    };
    fetchFAQs();
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const findBestMatch = (userInput) => {
    const query = userInput.toLowerCase();
    
    // Very simple keyword matching algorithm
    let bestMatch = null;
    let maxMatches = 0;

    if (!Array.isArray(faqs)) return null;

    for (const faq of faqs) {
      const keywords = faq.question.toLowerCase().split(' ').filter(w => w.length > 3);
      let matchCount = 0;
      
      keywords.forEach(keyword => {
        if (query.includes(keyword)) {
          matchCount++;
        }
      });

      if (matchCount > maxMatches) {
        maxMatches = matchCount;
        bestMatch = faq;
      }
    }

    // Require at least 1 keyword match to consider it a valid answer
    if (maxMatches > 0 && bestMatch) {
      return bestMatch.answer;
    }
    
    return null;
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { sender: 'user', text: userMessage }]);
    setInput('');

    // Simulate bot thinking delay
    setTimeout(() => {
      const answer = findBestMatch(userMessage);
      
      if (answer) {
        setMessages(prev => [...prev, { sender: 'bot', text: answer }]);
      } else {
        setMessages(prev => [...prev, { 
          sender: 'bot', 
          text: "I'm still learning and don't quite understand that yet. For immediate assistance, please chat with our live team on WhatsApp!",
          isFallback: true
        }]);
      }
    }, 600);
  };

  return (
    <div className="chatbot-wrapper fixed bottom-6 right-6 flex flex-col items-end urbanist">
      
      {isOpen && (
        <div className="chat-window bg-white w-80 sm:w-96 rounded-2xl shadow-2xl mb-4 border border-gray-200 overflow-hidden flex flex-col h-[500px] max-h-[80vh]">
          {/* Header */}
          <div className="bg-[#f06600] text-white p-4 flex justify-between items-center shadow-md z-10">
            <div>
              <h3 className="font-bold text-lg">Beta Support</h3>
              <p className="text-xs text-orange-100 flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                Online
              </p>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white hover:text-gray-200 transition">
              <FaTimes size={20} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="messages-container flex-1 p-4 overflow-y-auto bg-gray-50 flex flex-col gap-4">
            {messages.map((msg, index) => (
              <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                  msg.sender === 'user' 
                    ? 'bg-gray-800 text-white rounded-br-none' 
                    : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none shadow-sm'
                }`}>
                  {msg.text}
                  
                  {msg.isFallback && (
                    <a 
                      href="https://wa.me/923254543708" 
                      target="_blank" 
                      rel="noreferrer"
                      className="mt-3 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-xl font-bold transition shadow-sm"
                    >
                      <FaWhatsapp className="mr-2 text-lg" />
                      Live Chat on WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-gray-100">
            <form onSubmit={handleSend} className="flex relative">
              <input 
                type="text" 
                placeholder="Type your message..." 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="flex-1 border border-gray-300 rounded-full py-3 pl-4 pr-12 focus:outline-none focus:border-[#f06600] transition bg-gray-50 text-sm"
              />
              <button 
                type="submit" 
                className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition ${input.trim() ? 'bg-[#f06600] text-white shadow-md' : 'text-gray-400'}`}
                disabled={!input.trim()}
              >
                <FaPaperPlane size={14} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${isOpen ? 'bg-gray-800 text-white' : 'bg-[#f06600] text-white'}`}
      >
        {isOpen ? <FaTimes size={24} /> : <FaCommentDots size={28} />}
      </button>

    </div>
  );
};

export default Chatbot;
